import React, { createContext, useContext, useState, useCallback } from "react";

const STORAGE_KEY = "app_auth_token";

export function getToken() {
  return sessionStorage.getItem(STORAGE_KEY) || "";
}

export function setToken(token) {
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function clearToken() {
  sessionStorage.removeItem(STORAGE_KEY);
}

// Auth context — exposes reactive token state so hooks can re-fetch after login
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => getToken());

  const login = useCallback((newToken) => {
    setToken(newToken);
    setTokenState(newToken);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setTokenState("");
  }, []);

  return (
    <AuthContext.Provider value={{ token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Install a global fetch interceptor that adds the auth header
 * to all requests going to our API backend.
 */
export function installAuthInterceptor() {
  const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";
  const originalFetch = window.fetch;

  window.fetch = function (input, init = {}) {
    const url = typeof input === "string" ? input : input?.url || "";
    // Only attach auth to our own API
    const isOurApi = url.startsWith(API_BASE) || url.startsWith("/api");
    if (isOurApi) {
      const token = getToken();
      if (token) {
        init = { ...init };
        init.headers = new Headers(init.headers || {});
        if (!init.headers.has("Authorization")) {
          init.headers.set("Authorization", `Bearer ${token}`);
        }
      }
    }
    return originalFetch.call(this, input, init);
  };
}
